/**
 * Global Config Page
 *
 * Admin page for viewing the current global app configuration
 * being served from DynamoDB. Supports switching between
 * Mobile App and Web CMS configurations.
 */

import { useState } from 'react';
import { RefreshCw, Smartphone, Globe, AlertCircle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfigJsonViewer } from '@/components/global-config/config-json-viewer';
import { useGlobalConfig } from '@/hooks/use-global-config';
import type { ConfigSource } from '@/types/global-config';

/**
 * Loading Skeleton Component
 */
function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-48" />
        <div className="flex gap-2">
          <Skeleton className="h-9 w-32" />
          <Skeleton className="h-9 w-24" />
        </div>
      </div>
      {[1, 2, 3, 4, 5].map(i => (
        <Skeleton key={i} className="h-14 w-full" />
      ))}
    </div>
  );
}

/**
 * Error Alert Component
 */
function ErrorAlert({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>Error loading configuration</AlertTitle>
      <AlertDescription className="flex items-center justify-between">
        <span>{message}</span>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try Again
        </Button>
      </AlertDescription>
    </Alert>
  );
}

/**
 * Config Tab Content Component
 */
function ConfigTabContent({ source }: { source: ConfigSource }) {
  const { data, isLoading, isError, error, refetch, isFetching } = useGlobalConfig(source);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (isError) {
    return (
      <ErrorAlert
        message={error instanceof Error ? error.message : 'Failed to load configuration'}
        onRetry={() => refetch()}
      />
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {isFetching && (
        <div className="absolute top-0 right-0">
          <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}
      <ConfigJsonViewer documents={data.documents} count={data.count} />
    </div>
  );
}

/**
 * Global Config Page Component
 */
export default function GlobalConfigPage() {
  const [activeTab, setActiveTab] = useState<ConfigSource>('mobile');
  const mobileQuery = useGlobalConfig('mobile');
  const webQuery = useGlobalConfig('web');

  const handleRefresh = () => {
    if (activeTab === 'mobile') {
      mobileQuery.refetch();
    } else {
      webQuery.refetch();
    }
  };

  const isRefreshing =
    (activeTab === 'mobile' && mobileQuery.isFetching) ||
    (activeTab === 'web' && webQuery.isFetching);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-2xl">Global App Config</CardTitle>
              <CardDescription>
                View the current configuration being served from DynamoDB
              </CardDescription>
            </div>
            <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <Tabs value={activeTab} onValueChange={value => setActiveTab(value as ConfigSource)}>
            <TabsList className="mb-6">
              <TabsTrigger value="mobile" className="flex items-center gap-2">
                <Smartphone className="h-4 w-4" />
                Mobile App
              </TabsTrigger>
              <TabsTrigger value="web" className="flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Web CMS
              </TabsTrigger>
            </TabsList>

            <TabsContent value="mobile" className="mt-0">
              <ConfigTabContent source="mobile" />
            </TabsContent>

            <TabsContent value="web" className="mt-0">
              <ConfigTabContent source="web" />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
